// How much of the displayed history window is accounted for.
//
// Says what is covered rather than what is left. "7.8 years left" asks a driver
// to subtract in their head to work out how they are doing; "2 yr 2 mo of 10
// years" is the same fact stated as progress, which is what the bar beside it
// already shows. Both halves come from the same two numbers, so the words and
// the bar cannot disagree.

import { formatMonthCountLong } from 'src/utils/date/formatMonthPeriod'
import { MONTHS_PER_YEAR } from 'src/utils/date/monthPeriods'

export interface ProgressHeaderProps {
  /** Months inside the window accounted for. Satisfied by a MonthCoverage. */
  coveredMonths: number
  windowMonths: number
  windowLabel?: string
}

const windowLabel = (windowMonths: number): string => {
  const years = windowMonths / MONTHS_PER_YEAR
  if (!Number.isInteger(years)) {
    return `of the last ${windowMonths} months`
  }
  return `of the last ${years} ${years === 1 ? 'year' : 'years'}`
}

export const ProgressHeader = ({
  coveredMonths,
  windowMonths,
  windowLabel: label,
}: ProgressHeaderProps) => {
  const covered = Math.min(Math.max(coveredMonths, 0), windowMonths)
  const remaining = windowMonths - covered
  const complete = remaining <= 0
  const percent = windowMonths > 0 ? (covered / windowMonths) * 100 : 0

  return (
    <div className="border-b border-doubleNickel-gray-200 pb-2.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[13px] font-semibold text-doubleNickel-gray-900">
          {complete ? 'Complete' : formatMonthCountLong(covered)}
        </span>
        <span
          className={
            complete
              ? 'text-[12px] font-semibold text-doubleNickel-success-700'
              : 'text-[12px] text-doubleNickel-gray-600'
          }
        >
          {label ?? windowLabel(windowMonths)}
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={covered}
        aria-valuemin={0}
        aria-valuemax={windowMonths}
        aria-label="Employment history accounted for"
        className="mt-1.5 h-1.5 overflow-hidden rounded bg-doubleNickel-gray-100"
      >
        <div
          className={
            complete
              ? 'h-full rounded bg-doubleNickel-success-600'
              : 'h-full rounded bg-doubleNickel-brand-500'
          }
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  )
}
